import type {
  ContainerProps,
  DottedLineProps,
  ScoreIndicatorProps,
  ScoreLabelProps,
  ScoreLineContainerProps,
  ScoreLineProps,
} from './types'

import { cva } from 'class-variance-authority'

import Icon, { IconName } from '@/components/atomic/icon'
import { BodyXS, FontWeight } from '@/components/atomic/text'
import { cn } from '@/utils/shadcn/utils'

export function Container({ children, className }: ContainerProps) {
  return (
    <div
      className={cn(
        'relative w-full h-19.5 bg-secondary-background rounded-md flex items-center',
        className,
      )}
      style={{ padding: '12px min(14%, 70px)' }}
    >
      {children}
    </div>
  )
}

export function Track() {
  return (
    <div
      className="absolute left-0 w-full h-0.25 bg-moment-blue-grey-200 dark:bg-moment-blue-grey-1000 transform -translate-y-1/2"
      style={{
        maskImage:
          'linear-gradient(to right, transparent, black 20%, black 80%, transparent)',
      }}
    />
  )
}

export function ScoreLineContainer({ children }: ScoreLineContainerProps) {
  return (
    <div className="relative w-full flex items-center h-full">{children}</div>
  )
}

export function ScoreLine({ children }: ScoreLineProps) {
  return <div className="relative w-full h-0.25">{children}</div>
}

export function DottedLine({
  currentPosition,
  targetPosition,
  isWarning = false,
}: DottedLineProps) {
  const leftPosition = Math.max(0, Math.min(currentPosition, targetPosition))
  const rightPosition = Math.min(100, Math.max(currentPosition, targetPosition))

  return (
    <div
      className={cn(
        'absolute top-1/2 h-[1px] border-t-1 border-dashed',
        isWarning ? 'border-moment-red-700' : 'border-moment-teal-600',
      )}
      style={{
        left: `${leftPosition}%`,
        width: `${rightPosition - leftPosition}%`,
        transform: 'translateY(-50%)',
      }}
    />
  )
}

// ScoreIndicator variants
const scoreIndicatorVariants = cva(
  'absolute top-1/2 w-2.5 h-2.5 rounded-full -translate-x-1/2 -translate-y-1/2', // base styles
  {
    variants: {
      variant: {
        default:
          'border-1 border-primary-border bg-background dark:bg-moment-grey-800 z-0', // Used for min and max
        current: 'bg-moment-teal-600 z-20',
        proposal:
          'border-1 border-moment-orange-700 dark:border-moment-orange-500 bg-moment-orange-400/50 dark:bg-moment-orange-800/50 z-20',
        target: 'border-1 border-moment-teal-600 bg-background z-10',
      },
      warning: {
        true: 'bg-moment-red-700 z-20', // Warning style overrides variant colors
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      warning: false,
    },
  },
)

export function ScoreIndicator({
  position,
  variant,
  warning,
  className = '',
  ...props
}: ScoreIndicatorProps) {
  const clampedPosition = Math.max(0, Math.min(100, position))

  return (
    <div
      className={cn(
        scoreIndicatorVariants({
          variant,
          warning,
        }),
        className,
      )}
      style={{ left: `${clampedPosition}%` }}
      {...props}
    />
  )
}

// ScoreLabel variants
const scoreLabelVariants = cva(
  'absolute flex items-center gap-1 min-w-max px-1 pt-0.25 rounded-md border-1 select-none', // base styles
  {
    variants: {
      variant: {
        default:
          'border-primary-border bg-secondary-background z-10 hover:z-50', // For min/max
        current:
          'border-moment-teal-500 dark:border-moment-teal-800 bg-moment-teal-200 dark:bg-moment-teal-900 z-30 hover:z-50', // For current value
        proposal:
          'border-moment-orange-600 dark:border-moment-orange-900 bg-moment-orange-100 dark:bg-moment-orange-1000 z-20 hover:z-50', // For proposed value
        target:
          'border-moment-blue-grey-300 dark:border-moment-blue-grey-900 bg-secondary-background z-20 hover:z-50', // For target value
      },
      warning: {
        true: 'border-moment-red-700 dark:border-moment-red-800 bg-moment-red-100 dark:bg-moment-red-900', // overrides
        false: '',
      },
      placement: {
        top: 'top-0',
        bottom: 'bottom-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      warning: false,
      placement: 'bottom',
    },
  },
)

// label name (the part that says "Current", "Target", etc.
const labelNameVariants = cva('', {
  variants: {
    variant: {
      default: 'text-tertiary',
      current: 'text-moment-teal-800 dark:text-moment-teal-300',
      proposal: 'text-moment-orange-800 dark:text-moment-orange-400',
      target: 'text-secondary',
    },
    warning: {
      true: 'text-moment-red-700 dark:text-moment-red-200', // overrides
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})
const valueVariants = cva('', {
  variants: {
    variant: {
      default: 'text-secondary',
      current: 'text-moment-teal-800 dark:text-moment-teal-300',
      proposal: 'text-moment-orange-800 dark:text-moment-orange-400',
      target: 'text-primary',
    },
    warning: {
      true: 'text-moment-red-700 dark:text-moment-red-200', // overrides
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export function ScoreLabel({
  position,
  label,
  value,
  variant,
  warning,
  placement = 'bottom',
  labelClassName = '',
  className = '',
  ...props
}: ScoreLabelProps) {
  const clampedPosition = Math.max(0, Math.min(100, position))

  return (
    <div
      className={cn(
        scoreLabelVariants({
          variant,
          warning,
          placement,
        }),
        className,
      )}
      style={{
        left: `${clampedPosition}%`,
        transform: 'translateX(-50%)',
      }}
      {...props}
    >
      <BodyXS
        className={cn(labelNameVariants({ variant, warning }), labelClassName)}
      >
        {label}
      </BodyXS>
      <div className="flex items-center gap-1">
        <BodyXS
          className={cn(valueVariants({ variant, warning }))}
          fontWeight={FontWeight.Medium}
        >
          {value}
        </BodyXS>
        {warning && (
          <Icon
            className="w-3 h-3 text-moment-red-700"
            iconName={IconName.ExclamationTriangleIcon}
          />
        )}
      </div>
    </div>
  )
}
